import React, { useContext } from 'react';

import { ScrollRestoration, useLoaderData } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

import { loadedProducts } from './global-state';
import * as Product from './product';
import * as Algolia from './algolia';
import { Logo, Footer, PillButton, AppHeader } from './template';
import { BasketContext } from './context-basket';
import { CategoryBreadcrumb } from './category-breadcrumb';

export async function loader({ params }) {
  const id = params.id;
  let product = loadedProducts.find((p) => p.id === id);

  if (product) {
    const data = await Algolia.products.getObject(id, {
      attributesToRetrieve: [
        'description',
        'ingredients',
        'price-history',
        'slug',
        'zuper-categories',
      ],
    });
    product['description'] = data['description'];
    product['ingredients'] = data['ingredients'];
    product['price-history'] = data['price-history']; //TODO handle failure
    product['slug'] = data['slug'];
    product['zuper-categories'] = data['zuper-categories'];
  } else {
    product = await Algolia.products.getObject(
      //TODO handle failure
      id,
      {
        attributesToRetrieve: [
          'id',
          'title',
          'slug',
          'market',
          'prices',
          'photo-filename',
          'url',
          'collected-at',
          'description',
          'ingredients',
          'price-history',
          'zuper-categories',
        ],
      }
    );
  }

  const todaysDate = Product.formatDateToYYYYMMDD(product['collected-at']);
  if (product && product['price-history']) {
    if (product.prices?.['per-item']?.scalar && product['price-history']) {
      product['price-history']['per-item'][todaysDate] =
        product.prices['per-item'].scalar;
    }
  }

  return {
    id: id,
    product: product,
  };
}

export const priceHistoryOptions = {
  responsive: true,
  locale: 'el',
  scales: {
    x: {
      grid: {
        tickColor: '#000',
      },
      ticks: {
        maxRotation: 0,
        minRotation: 0,
        autoSkipPadding: 20,
      },
    },
    y: {
      ticks: {
        callback: function (value, index, ticks) {
          return Product.formatPrice({ price: value });
        },
      },
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    tooltip: {
      backgroundColor: '#000',
      displayColors: false,
      callbacks: {
        label: function (context) {
          return Product.formatPrice({ price: context.parsed.y });
        },
      },
    },
    legend: {
      display: false,
    },
  },
};

function PriceHistory({ product }) {
  if (
    !product ||
    !product['price-history'] ||
    !product['price-history']['per-item']
  ) {
    return (
      <div className="text-gray-500 text-sm">
        Η εξέλιξη τιμής είναι προσωρινά μη διαθέσιμη.
      </div>
    );
  }
  const history = product['price-history']['per-item'];
  const dates = Object.keys(history).sort();

  const data = {
    labels: dates.map(Product.formatPriceHistoryDate),
    datasets: [
      {
        label: 'Τιμή',
        data: dates.map((key) => history[key]),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
    ],
  };

  return <Line options={priceHistoryOptions} data={data} />;
}

export function ProductPage() {
  const { product } = useLoaderData();
  const basket = useContext(BasketContext);

  const marketTitle = Product.marketTitle(product) || 'σούπερ μάρκετ';
  const canonical = `https://www.zuper-market.gr${Product.internalURL(product)}`;
  // Real product images only: our hosted photo, else the market's own
  // CDN photo. Never the no-photo placeholder — Google requires the
  // image to depict the product, and a placeholder in structured data
  // risks a manual action rather than a missing-field warning.
  const photo = product['photo-filename']
    ? Product.photoURL(product)
    : product['market-photo-url'] || undefined;
  const price = product?.prices?.['per-item']?.scalar;

  // Product JSON-LD only when we have BOTH a real image and a price:
  // Google evaluates offer-bearing Product markup as a "Merchant
  // listing", where a missing image is a CRITICAL issue that blocks
  // rich results (the 2026-08 Search Console emails). The offers block
  // itself is load-bearing and must stay: product snippets require at
  // least one of offers/review/aggregateRating — offers is the only one
  // we have, and singular Offer (not AggregateOffer) is what the
  // price-drop enhancement needs. So a Product block without offers, or
  // with offers but no image, are BOTH structured-data errors — for
  // photo-less products (mostly glx/bzr, whose sites publish no usable
  // product images) the only clean state is no Product markup at all
  // (breadcrumb stays). Those pages can't win a rich result anyway.
  const productJsonLd =
    photo && price > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.title,
          sku: product.id,
          image: photo,
          ...(product.description && { description: product.description }),
          offers: {
            '@type': 'Offer',
            url: canonical,
            priceCurrency: 'EUR',
            price: String(price),
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: marketTitle },
          },
        }
      : undefined;

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Αρχική',
        item: 'https://www.zuper-market.gr/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Αναζήτηση',
        item: 'https://www.zuper-market.gr/search',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: product.title,
        item: canonical,
      },
    ],
  };

  return (
    <div>
      <Helmet>
        <title>
          Πόσο κάνει {product.title}; Τιμή στο {marketTitle} | zuper
        </title>
        <meta
          name="description"
          content={`Πόσο κάνει το ${product.title}; Δες την τρέχουσα τιμή στο ${marketTitle}, την εξέλιξη της τιμής και σύγκρινε με άλλα σούπερ μάρκετ στο zuper.`}
        />
        <meta
          name="keywords"
          content={`πόσο κάνει ${product.title}, ποσο κανει ${product.title}, poso kanei, posokanei, τιμή ${product.title}, σύγκριση τιμών σούπερ μάρκετ`}
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content={`${product.title} — τιμή στο ${marketTitle}`}
        />
        <meta
          property="og:description"
          content={`Τρέχουσα τιμή, ιστορικό και σύγκριση τιμών για ${product.title}.`}
        />
        {photo && <meta property="og:image" content={photo} />}
        {productJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(productJsonLd)}
          </script>
        )}
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <div className="pb-4 justify-center flex lg:flex hidden">
        <Logo size="4xl" />
      </div>

      <AppHeader />

      <div className="max-w-3xl mx-auto p-8">
        <CategoryBreadcrumb categories={product['zuper-categories']} />

        <h2 className="text-xl font-semibold">{product.title}</h2>
        <p className="text-sm text-gray-600 pt-1">
          Πόσο κάνει το {product.title} στο {marketTitle}; Δες παρακάτω την
          τρέχουσα τιμή και πώς εξελίχθηκε.
        </p>
        <span className="text-xs text-gray-400">Κωδικός: {product.id}</span>

        <div className="min-h-full">
          <div className="flex py-4">
            <div className="flex-none m-2">
              <img
                className="object-cover w-36 h-36"
                src={Product.photoURL(product)}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = Product.noPhoto;
                }}
              />
            </div>
            <div className="flex-grow pl-4">
              <div className="flex">
                <Product.MarketIcon market={product.market} />
                <span className="pl-2">{Product.marketTitle(product)}</span>
              </div>
              <div className="pt-4 pb-2">
                <Product.PriceTag product={product} />
              </div>
              <div className="text-gray-500 text-sm pb-4">
                Η τιμή ανανεώθηκε πριν{' '}
                {Product.formatDuration(
                  (Date.now() - product['collected-at']) / 1000
                )}
              </div>
            </div>
          </div>

          <div className="bottom-0 left-0 right-0 flex justify-center items-center space-x-3 py-4">
            <a href={Product.marketRedirectURL(product)} target="_blank">
              <PillButton variant="grey">Δες το στο κατάστημα</PillButton>
            </a>
            <Product.QuantityWidget
              addFun={() => basket.addOneItem(product)}
              removeFun={() => basket.removeOneItem(product)}
              quantity={
                basket?.products?.[product.id]?.['zuper-basket-quantity']
              }
            />
          </div>

          {product.description && (
            <>
              <h3 className="text-lg py-4">Περιγραφή</h3>
              <p className="text-sm">{product.description}</p>
            </>
          )}

          <h3 className="text-lg py-4">Εξέλιξη τιμής</h3>
          <PriceHistory product={product} />

          {product.ingredients && (
            <>
              <h3 className="text-lg py-4">Συστατικά</h3>
              <p className="text-sm">{product.ingredients}</p>
            </>
          )}
        </div>

        <div className="pt-32">
          <Footer />
        </div>
        <ScrollRestoration />
      </div>
    </div>
  );
}
